// src/pages/providers/6_clinicalInterview/config/diagnosticFormulationConfig.js

// Define the base/default configuration
const defaultConfig = {
  panels: {
    primaryDiagnosis: {
      visible: true,
      required: true,
      title: "Provisional Primary Diagnosis",
      subtitle: "Select the main condition that best accounts for the majority of the patient's symptoms and functional impairment at this stage [Required]"
    },
    comorbidDiagnoses: {
      visible: true,
      required: false,
      title: "Provisional Secondary and/or Comorbid Diagnoses",
      subtitle: "Select any additional conditions that may be present [Optional]"
    },
    differentialDiagnoses: {
      visible: true,
      required: false,
      title: "Differential Diagnoses",
      subtitle: "Select any other conditions that were considered [Optional]"
    }
  },
  navigation: {
    // Use ADHD diagnosis to determine next step
    useAdhdFlowDecision: true,
    // Routes for ADHD vs non-ADHD primary diagnosis
    adhdRoute: "/proforma/semi-structured-interview/symptoms",
    nonAdhdRoute: "/proforma/diagnostic-decision/primary-diagnosis",
    // Button text for ADHD vs non-ADHD primary diagnosis
    adhdButtonText: "Go to Semi-Structured Interview",
    nonAdhdButtonText: "Go to Diagnostic Decision"
  }
};

// SLaM specific configuration - hide differential diagnoses panel and always go to DD
const slamConfig = {
  ...defaultConfig,
  panels: {
    ...defaultConfig.panels,
    differentialDiagnoses: {
      ...defaultConfig.panels.differentialDiagnoses,
      visible: false
    }
  },
  navigation: {
    // Always go to diagnostic decision regardless of ADHD diagnosis
    useAdhdFlowDecision: false,
    // Single route for all diagnoses
    defaultRoute: "/proforma/diagnostic-decision/primary-diagnosis",
    // Single button text for all diagnoses
    defaultButtonText: "Go to Diagnostic Decision"
  }
};

// Export the configurations for different organizations
export const diagnosticFormulationConfig = {
  // Testing / Demos
  default: defaultConfig,
  "ThinkDivergent": defaultConfig,   
  "ThinkDivergent Admin": defaultConfig,
  // Customers - most use default, SLaM has custom config
  "Phoenix Mental Health Services": defaultConfig,    
  "Well Life Clinic": defaultConfig,
  "MDT Org": defaultConfig, 
  "The Spectrum Mind Clinic": defaultConfig,
  "SLaM": slamConfig, // Hide differential diagnoses panel
  "3 Borough Adult ADHD and Autism Service": slamConfig, // Hide differential diagnoses panel
  "Psychological Consultations": defaultConfig,
};

/**
 * @deprecated Use deriveDiagnosticFormulationConfig(workflowConfig) instead.
 * This map-based lookup is retained for any legacy callers that only have an
 * org name string.  New code should pass the resolved workflow config object.
 */
export const getDiagnosticFormulationConfig = (organizationName) => {
  return diagnosticFormulationConfig[organizationName] || diagnosticFormulationConfig.default;
};

/**
 * Derive the Diagnostic Formulation config from the centralised workflow
 * config object (returned by useWorkflowConfig or resolveWorkflowConfig).
 *
 * SSI-first orgs (e.g. SLaM) hide the differentials panel and always
 * navigate to DD regardless of the ADHD diagnosis.  All other orgs use
 * the standard three-panel layout with ADHD-dependent navigation.
 *
 * @param {Object} wf - Resolved workflow config with computed helpers
 * @returns {Object} DF config with panels and navigation properties
 */
export const deriveDiagnosticFormulationConfig = (wf) => {
  if (!wf) return defaultConfig;

  // SSI-first orgs (SLaM) always go to DD after CI DF, hide differentials panel.
  // Multi-step DD orgs route to the Review page first; otherwise to the ADHD page.
  if (wf.isSSIFirst) {
    if (wf.isFlatDifferentials) {
      const ddRoute = wf.hasMultiStepDD
        ? "/proforma/diagnostic-decision/mdt-review"
        : "/proforma/diagnostic-decision/adhd";
      const ddButtonText = wf.hasMultiStepDD
        ? "Go to Diagnostic Decision Review"
        : "Go to Diagnostic Decision";
      return {
        ...slamConfig,
        navigation: {
          ...slamConfig.navigation,
          defaultRoute: ddRoute,
          defaultButtonText: ddButtonText,
        },
      };
    }
    return slamConfig;
  }

  // CI-first flat-model orgs (non-deferred) use a single navigation path
  // to SSI since there is no PPD-based routing.  SSI is always expected
  // for flat-model orgs, so we route straight to the SSI module.
  if (wf.isFlatDifferentials) {
    return {
      ...defaultConfig,
      navigation: {
        useAdhdFlowDecision: false,
        defaultRoute: "/proforma/semi-structured-interview/symptoms",
        defaultButtonText: "Go to Semi-Structured Interview",
      },
    };
  }

  return defaultConfig;
};

/**
 * Debug utility function to help troubleshoot configuration issues
 * Usage in console: debugDiagnosticFormulationConfig('SLaM')
 */
export const debugDiagnosticFormulationConfig = (organizationName) => {
  const config = getDiagnosticFormulationConfig(organizationName);
  
  console.group(`🔍 Diagnostic Formulation Config Debug - ${organizationName}`);
  console.log('Using config:', organizationName in diagnosticFormulationConfig ? organizationName : 'default (fallback)');
  
  console.group('📋 Panel Visibility Analysis');
  Object.entries(config.panels).forEach(([panelKey, panelConfig]) => {
    console.group(`${panelConfig.title} (${panelKey})`);
    console.log('Visible:', panelConfig.visible);
    console.log('Required:', panelConfig.required);
    console.groupEnd();
  });
  console.groupEnd();
  
  console.group('🚀 Navigation Configuration');
  console.log('Use ADHD Flow Decision:', config.navigation.useAdhdFlowDecision);
  if (config.navigation.useAdhdFlowDecision) {
    console.log('ADHD Route:', config.navigation.adhdRoute);
    console.log('Non-ADHD Route:', config.navigation.nonAdhdRoute);
    console.log('ADHD Button Text:', config.navigation.adhdButtonText);
    console.log('Non-ADHD Button Text:', config.navigation.nonAdhdButtonText);
  } else {
    console.log('Default Route:', config.navigation.defaultRoute);
    console.log('Default Button Text:', config.navigation.defaultButtonText);
  }
  console.groupEnd();
  
  console.groupEnd();
  
  return config;
};

/**
 * Validates a navigation configuration object
 */
export const validateNavigationConfig = (navConfig, configName = 'config') => {
  const errors = [];
  
  if (typeof navConfig.useAdhdFlowDecision !== 'boolean') {
    errors.push(`${configName}.navigation.useAdhdFlowDecision must be a boolean`);
  }
  
  if (navConfig.useAdhdFlowDecision) {
    // Validate ADHD flow properties
    if (typeof navConfig.adhdRoute !== 'string') {
      errors.push(`${configName}.navigation.adhdRoute must be a string`);
    }
    if (typeof navConfig.nonAdhdRoute !== 'string') {
      errors.push(`${configName}.navigation.nonAdhdRoute must be a string`);
    }
    if (typeof navConfig.adhdButtonText !== 'string') {
      errors.push(`${configName}.navigation.adhdButtonText must be a string`);
    }
    if (typeof navConfig.nonAdhdButtonText !== 'string') {
      errors.push(`${configName}.navigation.nonAdhdButtonText must be a string`);
    }
  } else {
    // Validate default flow properties
    if (typeof navConfig.defaultRoute !== 'string') {
      errors.push(`${configName}.navigation.defaultRoute must be a string`);
    }
    if (typeof navConfig.defaultButtonText !== 'string') {
      errors.push(`${configName}.navigation.defaultButtonText must be a string`);
    }
  }
  
  return errors;
};

// Validate all configurations at module load (development mode only)
if (process.env.NODE_ENV === 'development') {
  Object.entries(diagnosticFormulationConfig).forEach(([orgName, config]) => {
    // Validate panel configuration
    const panelErrors = [];
    
    if (!config.panels || typeof config.panels !== 'object') {
      panelErrors.push(`${orgName}: panels must be an object`);
    } else {
      Object.entries(config.panels).forEach(([panelKey, panelConfig]) => {
        if (typeof panelConfig.visible !== 'boolean') {
          panelErrors.push(`${orgName}.panels.${panelKey}: visible must be a boolean`);
        }
        if (typeof panelConfig.required !== 'boolean') {
          panelErrors.push(`${orgName}.panels.${panelKey}: required must be a boolean`);
        }
        if (typeof panelConfig.title !== 'string') {
          panelErrors.push(`${orgName}.panels.${panelKey}: title must be a string`);
        }
        if (typeof panelConfig.subtitle !== 'string') {
          panelErrors.push(`${orgName}.panels.${panelKey}: subtitle must be a string`);
        }
      });
    }
    
    // Validate navigation configuration
    const navErrors = validateNavigationConfig(config.navigation, orgName);
    
    const allErrors = [...panelErrors, ...navErrors];
    
    if (allErrors.length > 0) {
      console.error(`❌ Configuration validation failed for ${orgName}:`);
      allErrors.forEach(error => console.error(`  - ${error}`));
    } else {
      // console.log(`✅ Configuration validation passed for ${orgName}`);
    }
  });
}

/**
 * Configuration Schema Documentation:
 * 
 * panels: Object containing configuration for each panel
 *   - visible: If true, panel is displayed to the user
 *   - required: If true, panel must be completed to proceed
 *   - title: Display title for the panel
 *   - subtitle: Descriptive text shown below the title
 * 
 * navigation: Object containing navigation behaviour configuration
 *   - useAdhdFlowDecision: If true, navigation depends on ADHD diagnosis
 *   For useAdhdFlowDecision = true (default providers):
 *     - adhdRoute: Route path for ADHD diagnosis
 *     - nonAdhdRoute: Route path for non-ADHD diagnosis  
 *     - adhdButtonText: Button text for ADHD diagnosis
 *     - nonAdhdButtonText: Button text for non-ADHD diagnosis
 *   For useAdhdFlowDecision = false (SLaM):
 *     - defaultRoute: Single route path for all diagnoses
 *     - defaultButtonText: Single button text for all diagnoses
 * 
 * To add a new organization:
 * 1. Create a new config object extending defaultConfig
 * 2. Override specific panel/navigation properties as needed
 * 3. Add the organization name mapping in diagnosticFormulationConfig
 * 
 * Example for hiding differential diagnoses and always going to DD:
 * const newOrgConfig = {
 *   ...defaultConfig,
 *   panels: {
 *     ...defaultConfig.panels,
 *     differentialDiagnoses: {
 *       ...defaultConfig.panels.differentialDiagnoses,
 *       visible: false
 *     }
 *   },
 *   navigation: {
 *     useAdhdFlowDecision: false,
 *     defaultRoute: "/proforma/diagnostic-decision/primary-diagnosis",
 *     defaultButtonText: "Go to Diagnostic Decision"
 *   }
 * };
 */ 